import React, { useEffect, useMemo, useState, useRef } from "react";
import { ArrowBack, DescriptionOutlined, DeleteOutlineOutlined, Add, Person } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { searchCustomers } from "../../api/customerApi";
import { crmLeadApi, crmOpportunityApi } from "../../api/crmApi";
import { getCylinders } from "../../api/cylinderMasterApi";
import { estimateApi } from "../../api";
import StateSelect from "../../components/StateSelect";
import "./EstimateQuotation.css";

const today = () => new Date().toISOString().slice(0, 10);
const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const safeArray = (value) => (Array.isArray(value) ? value : []);

const newCylinder = () => ({
  id: `${Date.now()}-${Math.random()}`,
  cylinderType: "",
  capacity: "",
  serialNo: "",
  qty: 1,
  rate: 0,
  gst: 18,
  cylinderName: "",
  batchNo: "",
  srno: "",
  cylinderCapacity: "",
});

const customerName = (c) =>
  c?.name || c?.displayName || c?.customer_name || c?.customerName || "";

function CustomerPicker({ value, onChange, onAddQuotationCustomer }) {
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState(null);
  const [localCustomers, setLocalCustomers] = useState([]);

  useEffect(() => {
    try {
      setLocalCustomers(safeArray(JSON.parse(localStorage.getItem("customers") || "[]")));
    } catch {
      setLocalCustomers([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchCustomers({ name: query.trim() });
        const apiRows = Array.isArray(response?.data)
          ? response.data
          : safeArray(response?.data?.data || response?.data?.customers);

        const q = query.trim().toLowerCase();
        const merged = [...apiRows, ...localCustomers].filter((row) =>
          customerName(row).toLowerCase().includes(q)
        );

        const seen = new Set();
        setCustomers(
          merged.filter((row) => {
            const key = String(row.id ?? customerName(row)).toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return Boolean(customerName(row));
          })
        );
      } catch {
        const q = query.trim().toLowerCase();
        setCustomers(
          localCustomers.filter((row) =>
            customerName(row).toLowerCase().includes(q)
          )
        );
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, query, localCustomers]);

  useEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 24);
      const left = Math.max(
        12,
        Math.min(rect.left, window.innerWidth - width - 12)
      );
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const showAbove = below < 260 && above > below;
      const maxHeight = Math.max(
        160,
        Math.min(300, showAbove ? above : below)
      );

      setStyle({
        position: "fixed",
        left,
        top: showAbove ? rect.top - maxHeight - 6 : rect.bottom + 6,
        width,
        maxHeight,
        zIndex: 10050,
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, customers.length]);

  useEffect(() => {
    const close = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const noResults = !loading && query.trim().length > 0 && customers.length === 0;

  const list = open && style ? (
    <div className="eq-customer-dropdown" style={style} ref={dropdownRef}>
      <div className="eq-customer-search">
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search customer..."
        />
      </div>

      {loading && <div className="eq-dropdown-state">Searching...</div>}

      {!loading && query.trim().length === 0 && (
        <div className="eq-dropdown-state">Type to search customers...</div>
      )}

      {noResults && (
        <div className="eq-dropdown-state">No customers found.</div>
      )}

      {!loading &&
        customers.map((customer, index) => (
          <button
            type="button"
            className="eq-customer-option"
            key={customer.id ?? `${customerName(customer)}-${index}`}
            onClick={() => {
              onChange(customerName(customer), customer);
              setOpen(false);
              setQuery("");
            }}
          >
            <strong>{customerName(customer)}</strong>
            <span>
              {customer.contactNumber ||
                customer.phone ||
                customer.email ||
                "Customer"}
            </span>
          </button>
        ))}

      {onAddQuotationCustomer && (
        <div className="eq-customer-dropdown-divider" />
      )}

      {onAddQuotationCustomer && (
        <button
          type="button"
          className="eq-add-quotation-customer"
          onClick={() => {
            onAddQuotationCustomer(query.trim());
            setOpen(false);
            setQuery("");
          }}
        >
          <span className="eq-add-customer-plus">+</span> New Customer
        </button>
      )}
    </div>
  ) : null;

  return (
    <>
      <div className="eq-customer-picker" ref={triggerRef}>
        <button
          type="button"
          className={`eq-field eq-customer-trigger ${
            value ? "has-value" : ""
          }`}
          onClick={() => setOpen((current) => !current)}
        >
          <span>{value || "Select or add a customer"}</span>
          <span className="eq-chevron" aria-hidden="true">⌄</span>
        </button>
      </div>
      {typeof document !== "undefined" &&
        createPortal(list, document.body)}
    </>
  );
}

function CylinderPicker({ value, onChange, cylinders }) {
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const filteredCylinders = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q
      ? cylinders.filter((cyl) =>
          (cyl.cylinderName || "").toLowerCase().includes(q) ||
          (cyl.srno || "").toLowerCase().includes(q) ||
          (cyl.batchNo || "").toLowerCase().includes(q)
        )
      : cylinders;

    const seen = new Set();
    return matched.filter((cyl) => {
      const name = (cyl.cylinderName || "").toLowerCase();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [cylinders, query]);

  const calcPos = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
  };

  useEffect(() => {
    if (!open) return;
    calcPos();
    const recalc = () => calcPos();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const dropdown = open ? createPortal(
    <div
      className="eq-cylinder-dropdown"
      ref={dropdownRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 10050 }}
    >
      <div className="eq-cylinder-search">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search cylinder..."
        />
      </div>
      {filteredCylinders.length === 0 && (
        <div className="eq-dropdown-state">No cylinders found.</div>
      )}
      {filteredCylinders.map((cyl) => (
        <button
          key={cyl.id}
          type="button"
          className="eq-cylinder-option"
          onClick={() => {
            onChange(cyl.cylinderName);
            setOpen(false);
            setQuery("");
          }}
        >
          <strong>{cyl.cylinderName || "Unnamed"}</strong>
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div className="eq-cylinder-picker" ref={triggerRef}>
      <button
        type="button"
        className={`eq-cylinder-trigger ${value ? "has-value" : ""}`}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{value || "Select cylinder"}</span>
        <span className="eq-chevron">⌄</span>
      </button>
      {dropdown}
    </div>
  );
}

function InlineCustomerForm({ initialName, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    name: initialName || "",
    mobile: "",
    email: "",
    address: "",
    gstin: "",
    state: "",
    city: "",
    pincode: "",
  });

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Customer name is required.");
      return;
    }
    if (!form.mobile.trim()) {
      toast.error("Mobile number is required.");
      return;
    }
    onSubmit(form);
  };

  const modal = createPortal(
    <div className="eq-modal-backdrop" onClick={onCancel}>
      <div className="eq-modal" onClick={(e) => e.stopPropagation()}>
        <div className="eq-modal-header">
          <h3>New Customer</h3>
          <button type="button" className="eq-modal-close" onClick={onCancel}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="eq-modal-body">
          <div className="eq-form-grid">
            <label className="eq-field-group">
              <span>Customer Name <b>*</b></span>
              <input
                className="eq-field"
                value={form.name}
                onChange={set("name")}
                placeholder="Customer name"
              />
            </label>
            <label className="eq-field-group">
              <span>Mobile Number <b>*</b></span>
              <input
                className="eq-field"
                type="tel"
                value={form.mobile}
                onChange={set("mobile")}
                placeholder="Enter mobile number"
              />
            </label>
            <label className="eq-field-group">
              <span>Email Address</span>
              <input
                className="eq-field"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="customer@example.com"
              />
            </label>
            <label className="eq-field-group">
              <span>GSTIN</span>
              <input
                className="eq-field"
                value={form.gstin}
                onChange={set("gstin")}
                placeholder="27XXXXXXXXXXXXXX"
              />
            </label>
            <div className="eq-field-group">
              <span>State</span>
              <StateSelect
                value={form.state}
                onChange={(v) => setForm((prev) => ({ ...prev, state: v }))}
                placeholder="Select State"
              />
            </div>
            <label className="eq-field-group">
              <span>City</span>
              <input
                className="eq-field"
                value={form.city}
                onChange={set("city")}
                placeholder="City"
              />
            </label>
            <label className="eq-field-group">
              <span>Pincode</span>
              <input
                className="eq-field"
                value={form.pincode}
                onChange={set("pincode")}
                placeholder="Pincode"
              />
            </label>
            <label className="eq-field-group eq-field-wide">
              <span>Address</span>
              <textarea
                className="eq-field eq-textarea"
                value={form.address}
                onChange={set("address")}
                placeholder="Enter complete customer address"
              />
            </label>
          </div>
          <div className="eq-modal-footer">
            <button type="button" className="eq-btn eq-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="eq-btn eq-primary">
              Add to Quotation
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );

  return modal;
}

export default function EstimateQuotation() {
  const navigate = useNavigate();
  const location = useLocation();
  const crmLead = location.state?.crmLead || null;
  const crmOpportunity = location.state?.opportunity || null;
  const crmLeadId = location.state?.crmLeadId ?? crmLead?.id ?? null;
  const crmOpportunityId = location.state?.crmOpportunityId ?? crmOpportunity?.id ?? null;
  const editData = location.state?.editData;

  const [quotationNo, setQuotationNo] = useState("");
  const [quotationDate, setQuotationDate] = useState(today());
  const [validUntil, setValidUntil] = useState("");

  // Fetch next quotation number on mount (only for new estimates)
  useEffect(() => {
    if (!editData?.id) {
      const fetchNextNumber = async () => {
        try {
          const response = await estimateApi.getNextQuotationNumber();
          setQuotationNo(response.data.nextQuotationNumber || `EST-${String(Date.now()).slice(-6)}`);
        } catch (error) {
          console.error("Failed to fetch next quotation number:", error);
          setQuotationNo(`EST-${String(Date.now()).slice(-6)}`);
        }
      };
      fetchNextNumber();
    }
  }, [editData?.id]);

  const [customer, setCustomer] = useState(() => {
    const seed = crmOpportunity || crmLead;
    if (!seed) {
      return {
        id: null,
        name: "",
        mobile: "",
        email: "",
        address: "",
        gstin: "",
        state: "",
        city: "",
        pincode: "",
        source: "",
      };
    }
    return {
      id: seed.customerId || null,
      name: seed.customerName || "",
      mobile: seed.mobile || "",
      email: seed.email || "",
      address: seed.address || "",
      gstin: "",
      state: seed.state || "",
      city: seed.city || "",
      pincode: "",
      source: seed.customerId ? "existing" : "",
    };
  });

  const [cylinders, setCylinders] = useState([newCylinder()]);
  const [availableCylinders, setAvailableCylinders] = useState([]);
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [inlineFormPrefill, setInlineFormPrefill] = useState("");

  const [notes, setNotes] = useState(() => {
    const seed = crmOpportunity || crmLead;
    if (!seed) return "";
    const ref = seed.leadId || seed.opportunityId || "";
    return seed.requirement ? `${ref ? `[${ref}] ` : ""}${seed.requirement}` : "";
  });
  const [terms, setTerms] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    const loadCylinders = async () => {
      try {
        const response = await getCylinders({ size: 1000 });
        const cylinderData = response?.data?.content || response?.data || [];
        const mappedCylinders = cylinderData.map(cyl => ({
          id: cyl.id,
          cylinderName: cyl.cylinderName || '',
          batchNo: cyl.batchNo || '',
          srno: cyl.srno || '',
          cylinderCapacity: cyl.cylinderCapacity || '',
        }));
        if (active) setAvailableCylinders(mappedCylinders);
      } catch (error) {
        console.error("Failed to load cylinders:", error);
        if (active) setAvailableCylinders([]);
      }
    };

    loadCylinders();
    return () => {
      active = false;
    };
  }, []);

  // Load estimate data when editing
  useEffect(() => {
    if (editData?.id) {
      const loadEstimate = async () => {
        try {
          const response = await estimateApi.getEstimateById(editData.id);
          const estimate = response.data;
          
          if (estimate) {
            setQuotationNo(estimate.quotationNo || "");
            setQuotationDate(estimate.quotationDate ? estimate.quotationDate.split('T')[0] : today());
            setValidUntil(estimate.validUntil ? estimate.validUntil.split('T')[0] : "");
            setNotes(estimate.notes || "");
            setTerms(estimate.terms || "");
            
            if (estimate.customer) {
              setCustomer({
                id: estimate.customer.id || null,
                name: estimate.customer.name || "",
                mobile: estimate.customer.contactNumber || estimate.customer.mobile || "",
                email: estimate.customer.email || "",
                address: estimate.customer.address || "",
                gstin: estimate.customer.gstin || "",
                state: estimate.customer.state || "",
                city: estimate.customer.city || "",
                pincode: estimate.customer.pincode || "",
                source: estimate.customer.id ? "existing" : "quotation",
              });
            }
            
            if (estimate.cylinders && estimate.cylinders.length > 0) {
              const mappedCylinders = estimate.cylinders.map((cyl, index) => ({
                id: cyl.id || `${Date.now()}-${index}-${Math.random()}`,
                cylinderType: cyl.cylinderType || "",
                cylinderName: cyl.cylinderName || "",
                capacity: cyl.capacity || "",
                cylinderCapacity: cyl.cylinderCapacity || "",
                serialNo: cyl.serialNo || "",
                srno: cyl.srno || "",
                batchNo: cyl.batchNo || "",
                qty: cyl.qty || 1,
                rate: cyl.rate || 0,
                gst: cyl.gst || 18,
              }));
              setCylinders(mappedCylinders);
            }
          }
        } catch (error) {
          console.error("Failed to load estimate:", error);
          toast.error("Failed to load estimate data");
        }
      };
      loadEstimate();
    }
  }, [editData]);

  const totals = useMemo(() => {
    let taxable = 0;
    let gst = 0;

    const rows = cylinders.map((row) => {
      const amount = Number(row.rate || 0) * Number(row.qty || 0);
      const gstAmount = (amount * Number(row.gst || 0)) / 100;
      taxable += amount;
      gst += gstAmount;

      return {
        ...row,
        amount,
        gstAmount,
        total: amount + gstAmount,
      };
    });

    return {
      rows,
      taxable,
      gst,
      grandTotal: taxable + gst,
    };
  }, [cylinders]);

  const selectCustomer = (name, record) => {
    if (!record) {
      setCustomer({
        id: null,
        name: "",
        mobile: "",
        email: "",
        address: "",
        gstin: "",
        state: "",
        city: "",
        pincode: "",
        source: "",
      });
      return;
    }

    setCustomer({
      id: record?.id ?? null,
      name: name,
      mobile: record?.contactNumber || record?.mobile || record?.phone || "",
      email: record?.email || "",
      address: record?.address || "",
      gstin: record?.gstin || record?.gstinNumber || "",
      state: record?.state || "",
      city: record?.city || "",
      pincode: record?.pincode || record?.pinCode || "",
      source: record?.id ? "existing" : "quotation",
    });
  };

  const handleAddQuotationCustomer = (prefillName) => {
    setInlineFormPrefill(prefillName || "");
    setShowInlineForm(true);
  };

  const handleInlineFormSubmit = (formData) => {
    setCustomer({
      id: null,
      name: formData.name,
      mobile: formData.mobile,
      email: formData.email || "",
      address: formData.address || "",
      gstin: formData.gstin || "",
      state: formData.state || "",
      city: formData.city || "",
      pincode: formData.pincode || "",
      source: "quotation",
    });
    setShowInlineForm(false);
  };

  const updateCylinder = (id, field, value) => {
    setCylinders((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const addCylinder = () => setCylinders((rows) => [...rows, newCylinder()]);

  const removeCylinder = (id) => {
    setCylinders((rows) =>
      rows.length > 1 ? rows.filter((row) => row.id !== id) : rows
    );
  };

  const save = async (status) => {
    if (!customer.name.trim()) {
      toast.error("Please select a customer.");
      return;
    }

    if (!customer.mobile.trim()) {
      toast.error("Customer mobile number is required.");
      return;
    }

    if (!quotationDate) {
      toast.error("Quotation date is required.");
      return;
    }

    const validCylinders = cylinders.filter(
      (row) => row.cylinderType.trim() || Number(row.rate || 0) > 0
    );

    if (!validCylinders.length) {
      toast.error("Please add at least one cylinder.");
      return;
    }

    setSaving(true);

    try {
      const isExistingCustomer = customer.source === "existing" && customer.id;
      const editData = location.state?.editData;

      const customerPayload = {
        name: customer.name,
        contactNumber: customer.mobile,
        mobile: customer.mobile,
        email: customer.email || "",
        address: customer.address || "",
        gstin: customer.gstin || "",
        state: customer.state || "",
        city: customer.city || "",
        pincode: customer.pincode || "",
      };

      // Only include id for existing customers (backend expects primitive int, can't accept null)
      if (isExistingCustomer) {
        customerPayload.id = customer.id;
      }

      const payload = {
        type: "Estimate / Quotation",
        quotationNo,
        quotationDate,
        validUntil: validUntil || null,
        status,
        customer: customerPayload,
        crmLeadId: crmLeadId || null,
        crmOpportunityId: crmOpportunityId || null,
        cylinders: totals.rows.map(row => ({
          cylinderType: row.cylinderType,
          cylinderName: row.cylinderName,
          capacity: row.capacity,
          cylinderCapacity: row.cylinderCapacity,
          serialNo: row.serialNo,
          srno: row.srno,
          batchNo: row.batchNo,
          qty: row.qty,
          rate: row.rate,
          gst: row.gst,
        })),
        notes: notes || "",
        terms: terms || "",
        taxableAmount: totals.taxable,
        gstAmount: totals.gst,
        totalAmount: totals.grandTotal,
      };

      let savedEstimate;
      if (editData?.id) {
        // Update existing estimate
        savedEstimate = await estimateApi.updateEstimate(editData.id, payload);
      } else {
        // Create new estimate
        savedEstimate = await estimateApi.createEstimate(payload);
      }

      const savedEstimateData = savedEstimate.data;

      if (crmLeadId || crmOpportunityId) {
        try {
          if (crmLeadId) {
            await crmLeadApi.update(crmLeadId, {
              status: "Quotation",
              ctsQuoteId: quotationNo,
            });
          }
          if (crmOpportunityId) {
            await crmOpportunityApi.linkQuotation(crmOpportunityId, {
              quotationNo,
              quotationId: savedEstimateData?.id,
            });
          }
        } catch {
          /* CRM link-back is best-effort */
        }
      }

      toast.success(
        status === "Draft"
          ? "Estimate saved as draft."
          : "Estimate / quotation saved successfully."
      );

      setTimeout(() => {
        navigate("/dashboard/estimates");
      }, 700);
    } catch (error) {
      console.error("Failed to save estimate:", error);
      toast.error("Unable to save estimate: " + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eq-page">
      <header className="eq-header">
        <div className="eq-header-left">
          <div className="eq-page-icon">
            <DescriptionOutlined />
          </div>
          <div className="eq-title-wrap">
            <h1>New Estimate / Quotation</h1>
            <p>Create a professional estimate for your customer</p>
          </div>
        </div>

        <button
          type="button"
          className="eq-header-back"
          onClick={() => navigate("/dashboard/estimates")}
          title="Back"
        >
          <ArrowBack />
        </button>
      </header>

      <main className="eq-shell">
        <section className="eq-card">
          <div className="eq-section-head">
            <div>
              <h2>Estimate Details</h2>
              <p>Basic quotation information</p>
            </div>
          </div>

          <div className="eq-form-grid eq-meta-grid">
            <label className="eq-field-group">
              <span>Estimate / Quotation No. <b>*</b></span>
              <input
                className="eq-field"
                value={quotationNo}
                onChange={(e) => setQuotationNo(e.target.value)}
                placeholder="EST-00001"
              />
            </label>

            <label className="eq-field-group">
              <span>Quotation Date <b>*</b></span>
              <input
                type="date"
                className="eq-field"
                value={quotationDate}
                onChange={(e) => setQuotationDate(e.target.value)}
              />
            </label>

            <label className="eq-field-group">
              <span>Valid Until</span>
              <input
                type="date"
                className="eq-field"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="eq-card">
          <div className="eq-section-head">
            <div>
              <h2>
                <Person /> Customer Details
              </h2>
              <p>Select an existing customer or add customer details specifically for this quotation.</p>
            </div>
            <span className="eq-required">* Required</span>
          </div>

          {showInlineForm ? (
            <InlineCustomerForm
              initialName={inlineFormPrefill}
              onSubmit={handleInlineFormSubmit}
              onCancel={() => setShowInlineForm(false)}
            />
          ) : (
            <>
              {customer.name && (
                <div className="eq-customer-source-row">
                  <span className="eq-customer-name-display">{customer.name}</span>
                  {customer.source === "existing" ? (
                    <span className="eq-badge eq-badge-existing">Existing Customer</span>
                  ) : customer.source === "quotation" ? (
                    <span className="eq-badge eq-badge-quotation">Quotation Customer</span>
                  ) : null}
                  <button
                    type="button"
                    className="eq-change-customer-btn"
                    onClick={() => selectCustomer("", null)}
                  >
                    Change
                  </button>
                </div>
              )}

              {!customer.name && (
                <div className="eq-form-grid">
                  <label className="eq-field-group eq-field-wide">
                    <span>Customer Name <b>*</b></span>
                    <CustomerPicker
                      value={customer.name}
                      onChange={selectCustomer}
                      onAddQuotationCustomer={handleAddQuotationCustomer}
                    />
                  </label>
                </div>
              )}

              {customer.name && (
                <div className="eq-form-grid">
                  <label className="eq-field-group">
                    <span>Mobile Number <b>*</b></span>
                    <input
                      className="eq-field"
                      type="tel"
                      value={customer.mobile}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, mobile: e.target.value }))
                      }
                      placeholder="Enter mobile number"
                    />
                  </label>

                  <label className="eq-field-group">
                    <span>Email Address</span>
                    <input
                      className="eq-field"
                      type="email"
                      value={customer.email}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="customer@example.com"
                    />
                  </label>

                  <label className="eq-field-group">
                    <span>GSTIN</span>
                    <input
                      className="eq-field"
                      value={customer.gstin}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, gstin: e.target.value }))
                      }
                      placeholder="27XXXXXXXXXXXXXX"
                    />
                  </label>

                  <div className="eq-field-group">
                    <span>State</span>
                    <StateSelect
                      value={customer.state}
                      onChange={(v) =>
                        setCustomer((prev) => ({ ...prev, state: v }))
                      }
                      placeholder="Select State"
                    />
                  </div>

                  <label className="eq-field-group">
                    <span>City</span>
                    <input
                      className="eq-field"
                      value={customer.city}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, city: e.target.value }))
                      }
                      placeholder="City"
                    />
                  </label>

                  <label className="eq-field-group">
                    <span>Pincode</span>
                    <input
                      className="eq-field"
                      value={customer.pincode}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, pincode: e.target.value }))
                      }
                      placeholder="Pincode"
                    />
                  </label>

                  <label className="eq-field-group eq-field-wide">
                    <span>Address</span>
                    <textarea
                      className="eq-field eq-textarea"
                      value={customer.address}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, address: e.target.value }))
                      }
                      placeholder="Enter complete customer address"
                    />
                  </label>
                </div>
              )}
            </>
          )}
        </section>

        <section className="eq-card eq-cylinder-card">
          <div className="eq-section-head">
            <div>
              <h2>Cylinder Details</h2>
              <p>Add cylinder information and pricing for this estimate.</p>
            </div>
            <button type="button" className="eq-outline-btn" onClick={addCylinder}>
              <Add /> Add Cylinder
            </button>
          </div>

          <div className="eq-table-wrap">
            <table className="eq-table eq-cylinder-table">
              <colgroup>
                <col className="eq-col-index" />
                <col className="eq-col-cylinder" />
                <col className="eq-col-rate" />
                <col className="eq-col-qty" />
                <col className="eq-col-gst" />
                <col className="eq-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product / Cylinder Name</th>
                  <th>Rate</th>
                  <th>Qty</th>
                  <th>GST %</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cylinders.map((row, index) => {
                  // Map each cylinder row to table row
                  return (
                    <tr key={row.id}>
                      <td className="eq-index">{index + 1}</td>
                      <td>
                        <CylinderPicker
                          value={row.cylinderType}
                          onChange={(val) => {
                            const selected = availableCylinders.find(
                              (cyl) => cyl.cylinderName === val
                            );
                            setCylinders((rows) =>
                              rows.map((item) =>
                                item.id === row.id
                                  ? {
                                      ...item,
                                      cylinderType: val,
                                      cylinderName: selected?.cylinderName || val,
                                      cylinderCapacity: selected?.cylinderCapacity || item.cylinderCapacity,
                                      capacity: selected?.cylinderCapacity || item.capacity,
                                      batchNo: selected?.batchNo || item.batchNo,
                                      serialNo: selected?.srno || item.serialNo,
                                      srno: selected?.srno || item.srno,
                                    }
                                  : item
                              )
                            );
                          }}
                          cylinders={availableCylinders}
                        />
                      </td>
                      <td>
                        <input
                          className="eq-table-input"
                          type="text"
                          inputMode="decimal"
                          value={row.rate}
                          onChange={(e) =>
                            updateCylinder(row.id, "rate", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="eq-table-input"
                          type="text"
                          inputMode="numeric"
                          value={row.qty}
                          onChange={(e) =>
                            updateCylinder(row.id, "qty", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <select
                          className="eq-table-input"
                          value={row.gst}
                          onChange={(e) =>
                            updateCylinder(row.id, "gst", e.target.value)
                          }
                        >
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                          <option value="28">28%</option>
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="eq-delete-btn"
                          onClick={() => removeCylinder(row.id)}
                          disabled={cylinders.length === 1}
                          title="Remove cylinder"
                        >
                          <DeleteOutlineOutlined />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button type="button" className="eq-add-row" onClick={addCylinder}>
            <Add /> Add new cylinder row
          </button>
        </section>

        <section className="eq-card eq-bottom-card">
          <div className="eq-bottom-grid">
            <div className="eq-notes-grid">
              <label className="eq-field-group">
                <span>Customer Notes</span>
                <textarea
                  className="eq-field eq-textarea eq-notes-area"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes that should appear on the estimate"
                />
              </label>

              <label className="eq-field-group">
                <span>Terms & Conditions</span>
                <textarea
                  className="eq-field eq-textarea eq-notes-area"
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  placeholder="Enter quotation terms and conditions"
                />
              </label>
            </div>

            <div className="eq-summary">
              <div className="eq-summary-row">
                <span>Subtotal</span>
                <strong>₹ {money(totals.taxable)}</strong>
              </div>
              <div className="eq-summary-row">
                <span>Total GST</span>
                <strong>₹ {money(totals.gst)}</strong>
              </div>
              <div className="eq-summary-grand">
                <span>Grand Total</span>
                <strong>₹ {money(totals.grandTotal)}</strong>
              </div>
            </div>
          </div>
        </section>
        <footer className="eq-actions">
          <button
            type="button"
            className="eq-btn eq-secondary"
            onClick={() => navigate("/dashboard/estimates")}
          >
            Cancel
          </button>

          <button
            type="button"
            className="eq-btn eq-secondary"
            onClick={() => save("Draft")}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save as Draft"}
          </button>

          <button
            type="button"
            className="eq-btn eq-primary"
            onClick={() => save("Open")}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Estimate"}
          </button>
        </footer>
      </main>
    </div>
  );
}
